import { useState } from 'react'
import { Info, Link2, Mailbox, Plus, Timer, X } from 'lucide-react'
import Button from '../../components/Button'
import Card from '../../components/Card'
import FormRow from '../../components/FormRow'
import IconButton from '../../components/IconButton'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { InfoNote, SwitchField } from '../../components/Form'
import { useToast } from '../../components/toastContext'
import {
  SETTING_KEYS,
  TOGGLE_KEYS,
  saveOutlookPlugin,
  savePhishingSetting,
  togglePhishingSetting,
} from './phishingService'
import { phishingSettingsStrings as S } from './strings'
import { serverText } from '../../lib/i18n'

const C = S.settings

/**
 * The stored value is a JSON array of header names — except when it is empty, which the
 * server writes as `{}` rather than `[]`. Anything that is not an array reads as none.
 */
function parseHeaders(raw) {
  try {
    const parsed = JSON.parse(raw || '[]')
    return Array.isArray(parsed) ? parsed.map(String) : []
  } catch {
    return []
  }
}

/**
 * إعدادات الإصطياد — the loose settings the legacy keeps on this screen's fifth tab, and the
 * only place in the product they can be changed.
 *
 * EACH SETTING SAVES ON ITS OWN, because each is its own endpoint: `toggle-setting` for the
 * two switches, `save-setting` for the three text values, `save-outlook-plugin` for the block
 * at the bottom. There is no "save the settings" action to build one big form around.
 *
 * The two switches write immediately — a switch that needs a second click to mean anything is
 * a switch that lies. The text fields carry their own Save, because typing is not a decision
 * until you say so.
 */
export default function PhishingEnginePanel({ settings, onChange }) {
  const toast = useToast()
  const [phishingInterval, setPhishingInterval] = useState(settings.phishingInterval)
  const [listenerInterval, setListenerInterval] = useState(settings.listenerInterval)
  const [url, setUrl] = useState(settings.phishingUrl)
  const [headers, setHeaders] = useState(() => parseHeaders(settings.skipHeaders))
  const [newHeader, setNewHeader] = useState('')
  const [outlook, setOutlook] = useState(settings.outlook)
  const [saving, setSaving] = useState('')

  const report = (outcome) => {
    if (!outcome.ok) {
      toast.error(outcome.message || S.actionFailed)
      return false
    }
    toast.success(serverText(outcome.message) || S.saved)
    return true
  }

  const toggle = async (key, field, next) => {
    onChange({ [field]: next })
    const outcome = await togglePhishingSetting(key, next)
    // Put it back if the server refused, rather than showing a state it does not hold.
    if (!report(outcome)) onChange({ [field]: !next })
  }

  const saveValue = async (key, value, field) => {
    setSaving(key)
    const outcome = await savePhishingSetting(key, value)
    setSaving('')
    if (report(outcome) && field) onChange({ [field]: value })
  }

  const saveHeaders = async (next) => {
    setHeaders(next)
    await saveValue(SETTING_KEYS.skipHeaders, JSON.stringify(next), 'skipHeaders')
  }

  const addHeader = async () => {
    const name = newHeader.trim()
    if (!name || headers.includes(name)) return
    setNewHeader('')
    await saveHeaders([...headers, name])
  }

  const saveOutlook = async () => {
    setSaving('outlook')
    const outcome = await saveOutlookPlugin(outlook)
    setSaving('')
    if (report(outcome)) onChange({ outlook })
  }

  return (
    <>
      <Card title={C.engine} subtitle={C.engineHint}>
        <SwitchField
          checked={settings.phishingEnabled}
          onChange={(next) => toggle(TOGGLE_KEYS.phishing, 'phishingEnabled', next)}
          label={C.phishingEnabled}
        />
        <FormRow label={C.phishingInterval} hint={C.intervalHint}>
          {(id) => (
            <TextField
              id={id}
              dir="ltr"
              icon={Timer}
              placeholder="00:05:00"
              value={phishingInterval}
              onChange={setPhishingInterval}
              trailing={
                <Button
                  variant="ghost"
                  disabled={saving === SETTING_KEYS.phishingInterval}
                  onClick={() =>
                    saveValue(SETTING_KEYS.phishingInterval, phishingInterval, 'phishingInterval')
                  }
                >
                  {S.save}
                </Button>
              }
            />
          )}
        </FormRow>
      </Card>

      <Card title={C.listener} subtitle={C.listenerHint}>
        <SwitchField
          checked={settings.listenerEnabled}
          onChange={(next) => toggle(TOGGLE_KEYS.listener, 'listenerEnabled', next)}
          label={C.listenerEnabled}
        />
        <FormRow label={C.listenerInterval} hint={C.intervalHint}>
          {(id) => (
            <TextField
              id={id}
              dir="ltr"
              icon={Mailbox}
              placeholder="00:05:00"
              value={listenerInterval}
              onChange={setListenerInterval}
              trailing={
                <Button
                  variant="ghost"
                  disabled={saving === SETTING_KEYS.listenerInterval}
                  onClick={() =>
                    saveValue(SETTING_KEYS.listenerInterval, listenerInterval, 'listenerInterval')
                  }
                >
                  {S.save}
                </Button>
              }
            />
          )}
        </FormRow>
      </Card>

      <Card
        title={C.url}
        subtitle={C.urlHint}
        footer={
          <Button
            disabled={saving === SETTING_KEYS.phishingUrl}
            onClick={() => saveValue(SETTING_KEYS.phishingUrl, url, 'phishingUrl')}
          >
            {S.save}
          </Button>
        }
      >
        <FormRow label={C.url}>
          {(id) => (
            <TextField id={id} dir="ltr" type="url" icon={Link2} value={url} onChange={setUrl} />
          )}
        </FormRow>
      </Card>

      {/* A JSON array on the wire, a list on screen — the legacy asks an admin to type the
          JSON by hand, which is how a stray comma switches the whole guard off. */}
      <Card title={C.skipHeaders} subtitle={C.skipHeadersHint}>
        <div className="ph__chips">
          {headers.length === 0 && <span className="ph__nil">{C.skipHeadersEmpty}</span>}
          {headers.map((header) => (
            <span key={header} className="ph__chip">
              <bdi dir="ltr">{header}</bdi>
              {/* Neutral, unlike the deletes in the tables. Red is now carried at rest so a
                  destructive row action can be told apart without hovering it — which is
                  right for a record and wrong for a chip, where it would turn a settings
                  card into a row of warnings over something you undo by typing it again. */}
              <IconButton
                icon={X}
                label={S.delete}
                tone="primary"
                onClick={() => saveHeaders(headers.filter((one) => one !== header))}
              />
            </span>
          ))}
        </div>
        <FormRow label={C.skipHeadersAdd}>
          {(id) => (
            <TextField
              id={id}
              dir="ltr"
              value={newHeader}
              onChange={setNewHeader}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  addHeader()
                }
              }}
              trailing={
                <Button variant="ghost" onClick={addHeader} disabled={!newHeader.trim()}>
                  <Plus size={16} strokeWidth={2} />
                </Button>
              }
            />
          )}
        </FormRow>
      </Card>

      <Card
        title={C.outlook}
        subtitle={C.outlookHint}
        footer={
          <Button disabled={saving === 'outlook'} onClick={saveOutlook}>
            {S.save}
          </Button>
        }
      >
        <FormRow label={C.outlookType}>
          {(id) => (
            <TextField
              id={id}
              dir="ltr"
              value={outlook.type}
              onChange={(value) => setOutlook((previous) => ({ ...previous, type: value }))}
            />
          )}
        </FormRow>
        <FormRow label={C.outlookMethod}>
          {(id) => (
            <TextField
              id={id}
              dir="ltr"
              value={outlook.reportingMethod}
              onChange={(value) =>
                setOutlook((previous) => ({ ...previous, reportingMethod: value }))
              }
            />
          )}
        </FormRow>
        <FormRow label={C.outlookEmail}>
          {(id) => (
            <TextField
              id={id}
              dir="ltr"
              type="email"
              value={outlook.reportingEmail}
              onChange={(value) =>
                setOutlook((previous) => ({ ...previous, reportingEmail: value }))
              }
            />
          )}
        </FormRow>
        <FormRow label={C.outlookDelete}>
          {(id) => (
            <Select
              id={id}
              value={outlook.deleteAfterReport}
              onChange={(value) =>
                setOutlook((previous) => ({ ...previous, deleteAfterReport: value }))
              }
              options={[
                { value: 'true', label: S.active },
                { value: 'false', label: S.inactive },
              ]}
            />
          )}
        </FormRow>
        {/* Said out loud because the API cannot express it: an empty value is SKIPPED, not
            stored, so neither of these two can be un-set once chosen. */}
        <InfoNote icon={Info}>
          <bdi>{C.outlookNote}</bdi>
        </InfoNote>
      </Card>
    </>
  )
}
